import { useState } from "react";
import { adminUpdate } from "../lib/api";
import { toast } from "sonner";
import { PRICING_OPTIONS } from "./PricingLegend";

export default function EditToolModal({ tool, categories, assetClasses, instruments = [], regions = [], sectors = [], userBases = [], delivery = [], proSegments = [], pricingModels = [], terminalTypes = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    title: tool.title || "",
    url: tool.url || "",
    description: tool.description || "",
    key_feature: tool.key_feature || "",
    category: tool.category || "",
    category_2: tool.category_2 || "",
    asset_class: tool.asset_class || "",
    pricing: tool.pricing || "$?",
    pricing_model: tool.pricing_model || "",
    audience: tool.audience || "",
    pro_segment: tool.pro_segment || "",
    builder: tool.builder || "",
    // Multi-selects stored as Set in state; serialized to comma-string
    // on save (backend accepts either format).
    instruments: new Set(Array.isArray(tool.instruments) ? tool.instruments : []),
    regions: new Set(Array.isArray(tool.regions) ? tool.regions : []),
    sectors: new Set(Array.isArray(tool.sectors) ? tool.sectors : []),
    user_base: new Set(Array.isArray(tool.user_base) ? tool.user_base : []),
    delivery: new Set(Array.isArray(tool.delivery) ? tool.delivery : []),
    is_open_source: !!tool.is_open_source,
    is_terminal: !!tool.is_terminal,
    terminal_types: new Set(Array.isArray(tool.terminal_types) ? tool.terminal_types : []),
    // Delivery-aware install / open snippet. Stays empty for plain web
    // SaaS; useful for self_host (`pip install openbb`), claude_skill
    // (Lemon-Squeezy import URL), api (curl one-liner), etc.
    install_command: tool.install_command || "",
    // Stored as a space/comma-delimited string in the form; backend
    // normalizes on save into ["AAPL","MSFT",…]. Empty = no tickers
    // (the default for broad-market tools).
    tickers: Array.isArray(tool.tickers) ? tool.tickers.join(", ") : "",
    tags: Array.isArray(tool.tags) ? tool.tags.join(", ") : "",
    affiliate_url: tool.affiliate_url || "",
    affiliated: !!tool.affiliated,
    is_owner: !!tool.is_owner,
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleSet = (k, v) => () =>
    setForm((s) => {
      const next = new Set(s[k]);
      if (next.has(v)) next.delete(v); else next.add(v);
      return { ...s, [k]: next };
    });

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        title: form.title.trim(),
        url: form.url.trim(),
        description: form.description.trim(),
        key_feature: form.key_feature.trim(),
        category: form.category,
        category_2: form.category_2 || "",
        asset_class: form.asset_class,
        pricing: form.pricing,
        pricing_model: form.pricing_model || "",
        // Empty string clears the value on the backend; backend treats
        // "" as null. Sending null would get stripped by exclude_none.
        audience: form.audience || "",
        // pro_segment only valid when audience=pro; backend also enforces.
        pro_segment: form.audience === "pro" ? (form.pro_segment || "") : "",
        builder: form.builder || "",
        instruments: Array.from(form.instruments).join(", "),
        regions: Array.from(form.regions).join(", "),
        sectors: Array.from(form.sectors).join(", "),
        user_base: Array.from(form.user_base).join(", "),
        delivery: Array.from(form.delivery).join(", "),
        is_open_source: form.is_open_source,
        is_terminal: form.is_terminal,
        terminal_types: form.is_terminal ? Array.from(form.terminal_types).join(", ") : "",
        install_command: form.install_command.trim() || "",
        tickers: form.tickers.trim(),
        tags: form.tags.trim(),
        // Admin-only monetization link — outbound clicks route through
        // /api/go/<slug> which 302s here when set. Empty string clears.
        affiliate_url: form.affiliate_url.trim() || "",
        affiliated: form.affiliated,
        is_owner: form.is_owner,
      };
      const updated = await adminUpdate(tool.id, patch);
      // For the local state echo, hand back arrays not the comma strings.
      const localEcho = {
        ...patch,
        category_2: patch.category_2 || null,
        instruments: Array.from(form.instruments),
        regions: Array.from(form.regions),
        sectors: Array.from(form.sectors),
        delivery: Array.from(form.delivery),
        user_base: Array.from(form.user_base),
        is_open_source: form.is_open_source,
        is_terminal: form.is_terminal,
        terminal_types: form.is_terminal ? Array.from(form.terminal_types) : [],
      };
      onSaved(updated || { id: tool.id, ...localEcho });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        ? `Save failed: ${detail}`
        : status
        ? `Save failed (HTTP ${status})`
        : "Save failed (no response — check network)";
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("EditToolModal save failed", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="edit-modal-overlay"
    >
      <div
        className="bg-white border border-black max-w-2xl w-full mt-8 mb-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-modal"
      >
        <div className="border-b border-black px-4 py-2 flex items-center justify-between">
          <span className="text-[13px] font-bold">edit: {tool.title}</span>
          <button className="tv-action-link" onClick={onClose} data-testid="edit-close-btn">[close]</button>
        </div>
        <div className="p-4 space-y-3 text-[12px]">
          <Row label="title">
            <input className="tv-input w-full" value={form.title} onChange={upd("title")} data-testid="edit-title" />
          </Row>
          <Row label="url">
            <input className="tv-input w-full" value={form.url} onChange={upd("url")} data-testid="edit-url" />
          </Row>
          <Row label="description">
            <textarea className="tv-input w-full" rows={3} value={form.description} onChange={upd("description")} data-testid="edit-description" />
          </Row>
          <Row label="key feature">
            <input className="tv-input w-full" value={form.key_feature} onChange={upd("key_feature")} data-testid="edit-key" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="primary function">
              <select className="tv-select w-full" value={form.category} onChange={upd("category")} data-testid="edit-category">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Row>
            <Row label="secondary function">
              <select className="tv-select w-full" value={form.category_2} onChange={upd("category_2")} data-testid="edit-category-2">
                <option value="">— none —</option>
                {categories.filter((c) => c !== form.category).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Row>
            <Row label="asset class">
              <select className="tv-select w-full" value={form.asset_class} onChange={upd("asset_class")} data-testid="edit-asset">
                {assetClasses.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Row>
            <Row label="pricing">
              <select className="tv-select w-full" value={form.pricing} onChange={upd("pricing")} data-testid="edit-pricing">
                {PRICING_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Row>
          </div>
          <Row label="pricing model (commitment shape — orthogonal to $-tier)">
            <select className="tv-select w-full" value={form.pricing_model} onChange={upd("pricing_model")} data-testid="edit-pricing-model">
              <option value="">— unset —</option>
              {pricingModels.map((pm) => (
                <option key={pm} value={pm}>{pm.replace("_", " ")}</option>
              ))}
            </select>
          </Row>
          <Row label="delivery (how the user accesses the tool — multi-select)">
            <div className="flex flex-wrap gap-2" data-testid="edit-delivery">
              {delivery.map((d) => {
                const on = form.delivery.has(d);
                const label =
                  d === "chatgpt_gpt" ? "GPT"
                  : d === "self_host" ? "self-host"
                  : d.replace(/_/g, " ");
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={toggleSet("delivery", d)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`edit-delivery-${d}`}
                  >
                    {on ? "[x] " : "[ ] "}{label}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="open source (source is public / self-hostable — a property, not a delivery channel)">
            <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" data-testid="edit-is-open-source-label">
              <input
                type="checkbox"
                checked={form.is_open_source}
                onChange={upd("is_open_source")}
                data-testid="edit-is-open-source"
                className="accent-black"
              />
              <span>open source / self-hostable (GitHub, pip, docker)</span>
            </label>
          </Row>
          <Row label="terminal (multi-function all-in-one platform — Koyfin, TIKR, Godel-style; orthogonal to function category)">
            <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none" data-testid="edit-is-terminal-label">
              <input
                type="checkbox"
                checked={form.is_terminal}
                onChange={upd("is_terminal")}
                data-testid="edit-is-terminal"
                className="accent-black"
              />
              <span>this is a terminal (gets the TERMINAL badge + shows in the terminals filter)</span>
            </label>
            {form.is_terminal && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="edit-terminal-types">
                {terminalTypes.map((tt) => {
                  const on = form.terminal_types.has(tt);
                  return (
                    <button
                      type="button"
                      key={tt}
                      onClick={toggleSet("terminal_types", tt)}
                      className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                      data-testid={`edit-terminal-type-${tt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {on ? "[x] " : "[ ] "}{tt}
                    </button>
                  );
                })}
              </div>
            )}
          </Row>
          <Row label="install / open snippet (delivery-aware CTA — e.g. `pip install openbb`, claude-skill import URL, curl one-liner)">
            <input
              className="tv-input w-full"
              value={form.install_command}
              onChange={upd("install_command")}
              placeholder="leave blank for plain web SaaS"
              data-testid="edit-install-command"
              maxLength={240}
            />
          </Row>
          <Row label="instruments (vehicles the tool offers — multi-select)">
            <div className="flex flex-wrap gap-2" data-testid="edit-instruments">
              {instruments.map((inst) => {
                const on = form.instruments.has(inst);
                return (
                  <button
                    type="button"
                    key={inst}
                    onClick={toggleSet("instruments", inst)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`edit-instrument-${inst.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{inst}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="regions (continental coverage — multi-select)">
            <div className="flex flex-wrap gap-2" data-testid="edit-regions">
              {regions.map((r) => {
                const on = form.regions.has(r);
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={toggleSet("regions", r)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`edit-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{r}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="sector (GICS industry focus — multi-select; leave empty if broad)">
            <div className="flex flex-wrap gap-2" data-testid="edit-sectors">
              {sectors.map((s) => {
                const on = form.sectors.has(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={toggleSet("sectors", s)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`edit-sector-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{s}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="user base (who it's for — multi-select; powers /for/<x> pages)">
            <div className="flex flex-wrap gap-2" data-testid="edit-user-bases">
              {userBases.map((u) => {
                const on = form.user_base.has(u);
                return (
                  <button
                    type="button"
                    key={u}
                    onClick={toggleSet("user_base", u)}
                    className={`tv-chip ${on ? "tv-chip-on" : ""}`}
                    data-testid={`edit-user-base-${u.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {on ? "[x] " : "[ ] "}{u}
                  </button>
                );
              })}
            </div>
          </Row>
          <Row label="audience">
            <select
              className="tv-select w-full"
              value={form.audience}
              onChange={upd("audience")}
              data-testid="edit-audience"
            >
              <option value="">— unset —</option>
              <option value="retail">retail (built for individual investors / traders)</option>
              <option value="pro">pro (built for professionals / institutions)</option>
            </select>
          </Row>
          {form.audience === "pro" && (
            <Row label="pro segment (which kind of pro buyer)">
              <select
                className="tv-select w-full"
                value={form.pro_segment}
                onChange={upd("pro_segment")}
                data-testid="edit-pro-segment"
              >
                <option value="">— unset —</option>
                {proSegments.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </Row>
          )}
          <Row label="builder">
            <select
              className="tv-select w-full"
              value={form.builder}
              onChange={upd("builder")}
              data-testid="edit-builder"
            >
              <option value="">— unset —</option>
              <option value="indie">indie (solo dev / hobby project / no monetization)</option>
              <option value="company">company (incorporated business / team)</option>
            </select>
          </Row>
          <Row label="tags (3-8, comma-separated; from controlled vocab — use [retag] to auto-fill)">
            <input
              className="tv-input w-full"
              value={form.tags}
              onChange={upd("tags")}
              placeholder="e.g. Screeners, Charts, Financials, Dividends, Stock Comparison"
              data-testid="edit-tags"
            />
          </Row>
          <Row label="tickers (optional — only for single-ticker trackers or named baskets, max 10)">
            <input
              className="tv-input w-full"
              value={form.tickers}
              onChange={upd("tickers")}
              placeholder="e.g. TMDX  ·  or:  AAPL, MSFT, GOOGL"
              data-testid="edit-tickers"
            />
          </Row>
          <Row label="affiliate URL (admin only — outbound clicks 302 here when set)">
            <input
              className="tv-input w-full"
              value={form.affiliate_url}
              onChange={upd("affiliate_url")}
              placeholder="https://example.com/?via=terminalvalue"
              data-testid="edit-affiliate-url"
              type="url"
            />
          </Row>
          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.affiliated} onChange={upd("affiliated")} data-testid="edit-affiliated" />
              <span>affiliated</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_owner} onChange={upd("is_owner")} data-testid="edit-is-owner" />
              <span>is_owner (founder/builder)</span>
            </label>
          </div>
        </div>
        <div className="border-t border-black px-4 py-2 flex justify-end gap-3">
          <button className="tv-action-link" onClick={onClose} disabled={saving} data-testid="edit-cancel">[cancel]</button>
          <button className="tv-btn" onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "[ saving... ]" : "[save]"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[#666] text-[11px] mb-1">{label}</span>
      {children}
    </label>
  );
}
